/**
 * ACTIVE grading scale — SINGLE resolution point on the web.
 *
 * The server already resolved the scale data-driven (`grading_model` +
 * `resolve_cohort_model` in Rust): an academy can DECLARE whatever scales it
 * wants and the engine respects them. The web didn't: it resolved via the legacy
 * key, which only knows the system's 4, and on top of that had `gradingScheme("ve-0-20")`
 * hand-written in two places. Result: a declared scale worked in
 * the engine and got ignored on screen.
 *
 * This hook is the client mirror of that resolution. Everything that shows or creates
 * grades must come from here — including the MAXIMUM default of a gradable
 * item, which is derived from the scale, not from a constant.
 */

import { type Accessor, createMemo } from "solid-js";
import { type GradingDisplayStrategy, resolveActiveGradingModel } from "@space/shared";
import { useGradingModels, useOrgSettings } from "./use-academy-data";
import { useActiveOrg } from "./use-active-org";
import { useCourseSettings } from "./use-course-modules";
import { useCohort } from "./use-cohort-data";

/**
 * Resolves the active scale with the server's precedence: `grading_model_id`
 * cohort → course → org (isolated per org), then the legacy
 * `grading_scheme` key in the same order, then the deployment default.
 *
 * `cohortId` is optional (in a course's authoring there's no cohort yet) and
 * `organizationId` too: without it the user's active org is used.
 */
export function useActiveGradingScale(args?: {
  organizationId?: Accessor<string | undefined>;
  courseId?: Accessor<string | undefined>;
  cohortId?: Accessor<string | undefined>;
}): Accessor<GradingDisplayStrategy> {
  const { activeOrgId } = useActiveOrg();
  const orgId = () => args?.organizationId?.() ?? activeOrgId();
  const cohortId = args?.cohortId ?? (() => undefined);
  const courseId = args?.courseId ?? (() => undefined);
  const cohort = useCohort(cohortId);
  const courseSettings = useCourseSettings(courseId);
  const orgSettings = useOrgSettings(orgId);
  const models = useGradingModels(orgId);

  const scale = createMemo(() =>
    resolveActiveGradingModel({
      models: models.data ?? [],
      organizationId: orgId(),
      modelIds: [
        cohort.data?.grading_model_id,
        courseSettings.data?.grading_model_id,
        orgSettings.data?.grading_model_id,
      ],
      legacyKeys: [
        cohort.data?.grading_scheme,
        courseSettings.data?.grading_scheme,
        orgSettings.data?.grading_scheme,
      ],
    }),
  );
  return scale;
}
